import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Container, Card, Button } from 'react-bootstrap';
import Breadcrumbs from '../../../Common/Breadcrumbs';
import ShimmerLoader from '../../../Common/ShimmerLoader';
import '../../../Common/ShimmerLoader.css'; // Ensure shimmer styles are loaded
import { getSellerLocationById, deleteSellerLocation, updateSellerLocationStatus } from '../../../api/shop/sellers/sellerLocationService';
import { getSellerById } from '../../../api/shop/sellers/sellerService';
import { getProductsByFilters } from '../../../api/shop/products/productService';
import { getOrdersByFiltersLegacy, calculateOrderSummary } from '../../../api/shop/orders/orderService';
import { SellerLocation } from '../../../api/shop/types/sellerTypes';
import { Product } from '../../../api/shop/types/productTypes';
import { Order, OrderSummary } from '../../../api/shop/types/orderTypes';
import { StatusBadge, FulfillmentBadge, TypeBadge } from '../../../Common/BadgeStyles';
import '../../../Common/BadgeStyles.css';
import { getImageUrl } from '../../../utils/imageUrl';
import LocationFormModal from './components/LocationFormModal';
import { DeleteModal } from '../../../Common/DeleteModal';
import { SellerSuccessModal } from './components/SellerSuccessModal';
import ShopTabs from '../common/components/ShopTabs';
import ProductSummaryCards from '../common/components/ProductSummaryCards';
import OrderSummaryCards from '../common/components/OrderSummaryCards';
import ProductsTable from '../common/components/ProductsTable';
import OrdersTable from '../common/components/OrdersTable';
import { useSelector } from 'react-redux';
import { hasAnyPermission, type User } from '../../../utils/roleBasedAccess';
import './SellerDetail.css';
// CSS for tabs is imported in ShopTabs component, no need to import here

const SellerLocationDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, role, permissions } = useSelector((state: any) => state.Login);
  const userData: User = useMemo(
    () => ({
      ...(user || {}),
      subRole: (user && user.subRole) || role,
      permissions: permissions,
    }),
    [user, role, permissions]
  );
  const hasManagePermissions = hasAnyPermission(userData, [
    'shop.admin',
    'shop.seller.manage',
  ]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [location, setLocation] = useState<SellerLocation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Initialize activeTab from URL params, default to 'products'
  const tabFromUrl = searchParams.get('tab') as 'products' | 'orders' | null;
  const [activeTab, setActiveTab] = useState<'products' | 'orders'>(
    tabFromUrl === 'products' || tabFromUrl === 'orders' ? tabFromUrl : 'products'
  );
  
  // Products state
  const [products, setProducts] = useState<Product[]>([]);
  const [allProductsForSummary, setAllProductsForSummary] = useState<Product[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsPage, setProductsPage] = useState(1);
  const [productsTotalPages, setProductsTotalPages] = useState(1);
  const [productsTotalCount, setProductsTotalCount] = useState(0);
  const [productsActiveCount, setProductsActiveCount] = useState<number | undefined>(undefined);
  const [productsInactiveCount, setProductsInactiveCount] = useState<number | undefined>(undefined);
  
  // Orders state
  const [orders, setOrders] = useState<Order[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersPage, setOrdersPage] = useState(1);
  const [ordersTotalPages, setOrdersTotalPages] = useState(1);
  const [ordersTotalCount, setOrdersTotalCount] = useState(0);
  const [orderSummary, setOrderSummary] = useState<OrderSummary>({
    confirmed: 0,
    readyForPickup: 0,
    delivered: 0,
    returned: 0,
    replacement: 0,
  });
  
  // Modal states
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isUpdateOperation, setIsUpdateOperation] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchLocation = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const locationData = await getSellerLocationById(id);
      setLocation(locationData);
    } catch (err: any) {
      setError(err.message || 'Failed to load location details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchProducts = useCallback(async () => {
    if (!id) return;
    try {
      setProductsLoading(true);
      const response = await getProductsByFilters({
        pageNum: productsPage,
        sellerLocation: id,
      });
      setProducts(response.paginatedResults || []);
      setProductsTotalPages(response.totalPages || 1);
      setProductsTotalCount(response.totalCount || 0);
      
      // Use API-provided summary counts if available
      if (response.activeCount !== undefined && response.inactiveCount !== undefined) {
        setProductsActiveCount(response.activeCount);
        setProductsInactiveCount(response.inactiveCount);
        setAllProductsForSummary(response.paginatedResults || []);
      } else if (productsPage === 1 && response.totalCount > 0) {
        // Fallback: Calculate from products if API doesn't provide counts
        setProductsActiveCount(undefined);
        setProductsInactiveCount(undefined);
        try {
          // Fetch all products (with reasonable limit)
          const allProductsResponse = await getProductsByFilters({
            sellerLocation: id,
            pageNum: 1,
          });
          setAllProductsForSummary(allProductsResponse.paginatedResults || []);
        } catch (summaryErr) {
          // If fetching all fails, use current page data
          setAllProductsForSummary([]);
        }
      }
    } catch (err: any) {
      // Error handled silently
    } finally {
      setProductsLoading(false);
    }
  }, [id, productsPage]);

  const fetchOrders = useCallback(async () => {
    if (!id) return;
    try {
      setOrdersLoading(true);
      const response = await getOrdersByFiltersLegacy({
        pageNum: ordersPage,
        sellerLocation: id,
      });
      const ordersData = response.paginatedResults || [];
      setOrders(ordersData);
      setOrdersTotalPages(response.totalPages || 1);
      setOrdersTotalCount(response.totalCount || 0);
      
      const summary = calculateOrderSummary(ordersData);
      setOrderSummary(summary);
    } catch (err: any) {
      // Error handled silently
    } finally {
      setOrdersLoading(false);
    }
  }, [id, ordersPage]);

  useEffect(() => {
    fetchLocation();
  }, [fetchLocation]);

  // Sync activeTab with URL params when they change (e.g., browser back/forward)
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab') as 'products' | 'orders' | null;
    if (tabFromUrl === 'products' || tabFromUrl === 'orders') {
      if (activeTab !== tabFromUrl) {
        setActiveTab(tabFromUrl);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    if (activeTab === 'products' && id) {
      fetchProducts();
    }
  }, [activeTab, id, productsPage, fetchProducts]);

  useEffect(() => {
    if (activeTab === 'orders' && id) {
      fetchOrders();
    }
  }, [activeTab, id, ordersPage, fetchOrders]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return dateString;
    }
  };

  const getLocationTypeLabel = (type: string) => {
    return type === 'ACADEMY' ? 'Academy' : 'Warehouse';
  };

  const [sellerDetails, setSellerDetails] = useState<any>(null);

  const getSellerId = () => {
    if (!location?.seller) return 'N/A';
    if (typeof location.seller === 'string') {
      // If seller is just an ID string, use sellerDetails if available
      if (sellerDetails?.id) {
        return sellerDetails.id;
      }
      return location.seller; // Fallback to MongoDB ID
    }
    // If seller is an object, return the id field (not _id)
    return (location.seller as any).id || (location.seller as any)._id || 'N/A';
  };

  // Fetch seller details if seller is just an ID string
  useEffect(() => {
    if (location?.seller) {
      if (typeof location.seller === 'string') {
        // Seller is just an ID, fetch seller details
        getSellerById(location.seller)
          .then((seller) => {
            setSellerDetails(seller);
          })
          .catch((err) => {
            // Error handled silently
          });
      } else {
        // Seller is already populated, use it directly
        setSellerDetails(location.seller);
      }
    }
  }, [location?.seller]);

  const handleEditLocation = () => {
    if (!location) return;
    if (!hasManagePermissions) return;
    setShowFormModal(true);
  };

  const handleFormSuccess = () => {
    setIsUpdateOperation(true);
    setSuccessMessage('Seller location has been updated successfully');
    setShowFormModal(false);
    setShowSuccessModal(true);
    fetchLocation();
    if (activeTab === 'products') {
      fetchProducts();
    } else if (activeTab === 'orders') {
      fetchOrders();
    }
  };

  const handleDeleteClick = () => {
    if (!hasManagePermissions) return;
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!location) return;
    if (!hasManagePermissions) return;
    try {
      setDeleting(true);
      await deleteSellerLocation(location._id);
      setShowDeleteModal(false);
      // Navigate back to seller detail page
      const sellerId = typeof location.seller === 'string' ? location.seller : location.seller._id;
      navigate(`/shop-services/sellers/${sellerId}`);
    } catch (err: any) {
      // Error handled silently
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!location) return;
    if (!hasManagePermissions) return;
    
    const newStatus = location.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    
    try {
      await updateSellerLocationStatus(location._id, newStatus);
      // Refresh location data to reflect the change
      fetchLocation();
    } catch (error) {
      // Error handled silently
    }
  };

  const calculateProductSummary = () => {
    // Use API-provided counts if available, otherwise calculate from products
    if (productsActiveCount !== undefined && productsInactiveCount !== undefined) {
      return {
        total: productsTotalCount,
        active: productsActiveCount,
        inactive: productsInactiveCount,
      };
    }
    
    // Fallback: Calculate from products
    const productsToCount = allProductsForSummary.length > 0 ? allProductsForSummary : products;
    return {
      total: productsTotalCount,
      active: productsToCount.filter(p => p.status === 'ACTIVE').length,
      inactive: productsToCount.filter(p => p.status === 'INACTIVE').length,
    };
  };

  if (loading) {
    return (
      <div className="page-content" style={{ marginTop: '20px' }}>
        <Container fluid>
          {/* Breadcrumbs Shimmer */}
          <div className="mb-3" style={{ padding: '0 15px' }}>
            <div className="placeholder" style={{ height: '24px', width: '400px', borderRadius: '4px' }}></div>
          </div>
          
          {/* Header Shimmer */}
          <div style={{ padding: '0 15px', marginBottom: '24px' }}>
            <div className="d-flex align-items-center" style={{ gap: '16px' }}>
              <div className="placeholder" style={{ width: '40px', height: '40px', borderRadius: '4px' }}></div>
              <div className="placeholder" style={{ height: '32px', width: '200px', borderRadius: '4px' }}></div>
            </div>
          </div>
          
          {/* Location Details Card Shimmer */}
          <div style={{ padding: '0 15px', marginBottom: '32px' }}>
            <Card className="seller-detail-card">
              <Card.Body>
                <div className="placeholder" style={{ height: '250px', borderRadius: '8px' }}></div>
              </Card.Body>
            </Card>
          </div>
          
          {/* Tabs Shimmer */}
          <div style={{ padding: '0 15px', marginBottom: '24px' }}>
            <div className="d-flex gap-3">
              <div className="placeholder" style={{ height: '40px', width: '120px', borderRadius: '4px' }}></div>
              <div className="placeholder" style={{ height: '40px', width: '120px', borderRadius: '4px' }}></div>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  const productSummary = calculateProductSummary();

  return (
    <div className="page-content seller-detail-page" style={{ marginTop: '20px' }}>
      <Container fluid>
        {/* Breadcrumbs */}
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs items={[
            { label: 'Shop Services', onClick: () => navigate('/shop-services/sellers') },
            { label: 'Sellers', onClick: () => navigate('/shop-services/sellers') },
            { 
              label: typeof location?.seller === 'object' && location.seller 
                ? location.seller.name 
                : 'Seller',
              onClick: () => {
                const sellerId = location && typeof location.seller === 'string' 
                  ? location.seller 
                  : (location?.seller as any)?._id;
                if (sellerId) {
                  navigate(`/shop-services/sellers/${sellerId}`);
                }
              }
            },
            { label: location?.name || location?.locationCode || 'Location Details', active: true }
          ]} />
        </div>

        {/* Page Header with Back Button */}
        <div style={{ padding: '0 15px', marginBottom: '24px' }}>
          <div className="d-flex align-items-center" style={{ gap: '16px' }}>
            <button 
              className="back-button"
              onClick={() => {
                const sellerId = location && typeof location.seller === 'string' 
                  ? location.seller 
                  : (location?.seller as any)?._id;
                if (sellerId) {
                  navigate(`/shop-services/sellers/${sellerId}`);
                } else {
                  navigate('/shop-services/sellers');
                }
              }}
            >
              <i className="ri-arrow-left-line"></i>
            </button>
            <h1 className="page-title mb-0">Seller Locations</h1>
          </div>
        </div>

        {/* Location Details Section */}
        <div style={{ padding: '0 15px', marginBottom: '32px' }}>
          <Card className="seller-detail-card">
            <Card.Body>
              {location ? (
                <>
                  {/* Header with Title and Buttons */}
                  <div className="seller-detail-header">
                    <div className="d-flex align-items-center gap-2">
                      <h3 className="seller-detail-title mb-0">{location.name || location.locationCode}</h3>
                      <StatusBadge status={location.status as 'ACTIVE' | 'INACTIVE'} />
                    </div>
                    {hasManagePermissions && (
                      <div className="seller-detail-actions">
                        <Button
                          variant="primary"
                          className="seller-detail-action-btn"
                          onClick={handleEditLocation}
                        >
                          Edit Location
                        </Button>
                        <Button
                          variant="outline-warning"
                          className="seller-detail-action-btn"
                          onClick={handleToggleStatus}
                        >
                          {location.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
                        </Button>
                        <Button
                          variant="outline-danger"
                          className="seller-detail-action-btn seller-delete-btn"
                          onClick={handleDeleteClick}
                        >
                          <img 
                            src={getImageUrl('admin/static-assets/icons/general/delete-icon.svg')} 
                            alt="Delete" 
                            style={{ width: '16px', height: '16px' }}
                          />
                        </Button>
                      </div>
                    )}
                  </div>
                  
                  {/* Divider */}
                  <hr className="seller-detail-divider" />
                  
                  {/* Location Information - Horizontal Layout */}
                  {/* First Row: Seller ID, Location Code, Type */}
                  <div className="seller-detail-info" style={{ marginBottom: '24px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '24px' }}>
                    <div className="seller-detail-meta-item">
                      <span className="seller-detail-label">Seller ID:</span>
                      <span className="seller-detail-value">
                        {getSellerId()}
                      </span>
                    </div>
                    <div className="seller-detail-meta-item">
                      <span className="seller-detail-label">Location Code:</span>
                      <span className="seller-detail-value">{location.locationCode}</span>
                    </div>
                    <div className="seller-detail-meta-item">
                      <span className="seller-detail-label">Type:</span>
                      <div className="seller-detail-badge-wrapper">
                        <TypeBadge type={location.type} />
                      </div>
                    </div>
                  </div>
                  
                  {/* Second Row: Address, Fulfillment Capabilities, Created At */}
                  <div className="seller-detail-info" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '24px' }}>
                    <div className="seller-detail-meta-item">
                      <span className="seller-detail-label">Address:</span>
                      <div className="seller-detail-value" style={{ display: 'flex', flexDirection: 'column', gap: '4px', lineHeight: '1.5' }}>
                        {location.address?.line1 && (
                          <span>{location.address.line1}</span>
                        )}
                        <span>
                          {location.address?.city && `${location.address.city}, `}
                          {location.address?.state && `${location.address.state}`}
                          {location.address?.pincode && ` - ${location.address.pincode}`}
                        </span>
                      </div>
                    </div>
                    <div className="seller-detail-meta-item">
                      <span className="seller-detail-label">Fulfillment Capabilities:</span>
                      <div className="seller-detail-fulfillment-badges">
                        {(() => {
                          const sortedCapabilities = [...(location.fulfillmentCapabilities || [])].sort((a, b) => {
                            if (a === 'PICKUP') return -1;
                            if (b === 'PICKUP') return 1;
                            return 0;
                          });
                          return sortedCapabilities.map((capability) => (
                            <FulfillmentBadge 
                              key={capability} 
                              capability={capability as 'PICKUP' | 'DELIVERY'} 
                            />
                          ));
                        })()}
                      </div>
                    </div>
                    <div className="seller-detail-meta-item">
                      <span className="seller-detail-label">Created At:</span>
                      <span className="seller-detail-value">{formatDate(location.createdAt)}</span>
                    </div>
                  </div>
                </>
              ) : (
                <div className="seller-detail-no-data">
                  <div className="seller-detail-no-data-icon">
                    <i className="ri-information-line"></i>
                  </div>
                  <h4 className="seller-detail-no-data-title">Data Not Available</h4>
                  <p className="seller-detail-no-data-message">
                    {error || 'Location details could not be loaded. Please try again later.'}
                  </p>
                </div>
              )}
            </Card.Body>
          </Card>
        </div>

        {/* Tabs Section */}
        <div style={{ padding: '0 15px' }}>
          <ShopTabs
            tabs={[
              { key: 'products', label: 'Products' },
              { key: 'orders', label: 'Orders' },
            ]}
            activeTab={activeTab}
            onTabChange={(tab) => {
              const newTab = tab as 'products' | 'orders';
              setActiveTab(newTab);
              // Update URL params to persist tab state
              const params = new URLSearchParams(searchParams);
              params.set('tab', newTab);
              setSearchParams(params, { replace: true });
            }}
          />

          {/* Tab Content */}
          <div className="tab-content-container">
            {activeTab === 'products' && (
              <>
                <ProductSummaryCards
                  totalCount={productSummary.total}
                  activeCount={productSummary.active}
                  inactiveCount={productSummary.inactive}
                  loading={productsLoading}
                />
                <ProductsTable
                  products={products}
                  loading={productsLoading}
                  pagination={{
                    page: productsPage,
                    totalPages: productsTotalPages,
                    totalCount: productsTotalCount,
                  }}
                  onPageChange={setProductsPage}
                />
              </>
            )}

            {activeTab === 'orders' && (
              <>
                <OrderSummaryCards
                  summary={orderSummary}
                  loading={ordersLoading}
                />
                <OrdersTable
                  orders={orders}
                  loading={ordersLoading}
                  pagination={{
                    page: ordersPage,
                    totalPages: ordersTotalPages,
                    totalCount: ordersTotalCount,
                  }}
                  onPageChange={setOrdersPage}
                />
              </>
            )}
          </div>
        </div>
      </Container>

      {/* Form Modal */}
      {location && (
        <LocationFormModal
          show={showFormModal}
          onHide={() => setShowFormModal(false)}
          sellerId={typeof location.seller === 'string' ? location.seller : location.seller._id}
          location={location}
          onSuccess={handleFormSuccess}
          sellerFulfillmentCapabilities={location.fulfillmentCapabilities || []}
        />
      )}

      {/* Delete Modal */}
      <DeleteModal
        show={showDeleteModal}
        handleClose={() => setShowDeleteModal(false)}
        deleteModalFunction={handleDeleteConfirm}
        title="Delete Seller Location"
        message={`Are you sure you want to delete location "${location?.locationCode || location?.name || 'this location'}"? This action cannot be undone.`}
        isLoading={deleting}
      />

      {/* Success Modal */}
      <SellerSuccessModal
        show={showSuccessModal}
        handleClose={() => {
          setShowSuccessModal(false);
          setSuccessMessage('');
          setIsUpdateOperation(false);
        }}
        title={isUpdateOperation ? 'Seller Location Updated Successfully' : 'Seller Location Created Successfully'}
        message={successMessage}
      />
    </div>
  );
};

export default SellerLocationDetailPage;
